import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import type { BuyingOrderLineType } from './buyingOrdersUrl';
import { classifyLine } from './lineGroups';

const NEED_ID_PREFIX = 'need:';

/** A need row's id, `need:<type>:<warehouseId>:<productKey>` (see needIdOf). */
export const NEED_ID_PATTERN = /^need:(back_order|potf):[0-9a-f-]{36}:.+$/;

/**
 * One item × location need with several lines behind it (GEN-2938): its
 * sales-order lines, and for a back order also the item × location's stock
 * lines, so one order can cover both. The board shows it as one row with its
 * lines underneath.
 */
export interface NeedGroup {
  /** `need:<type>:<warehouseId>:<productKey>`; the need row's id. */
  id: string;
  type: BuyingOrderLineType;
  /** SO lines, oldest requested date first, then stock lines. */
  lines: AssignmentLine[];
  /** The lines a need-row action reaches: not trashed, not on an order. */
  open: AssignmentLine[];
  /** How many of `lines` are sales-order lines. */
  salesOrderCount: number;
}

export interface NeedRows {
  /** Table rows: each need row followed by its SO lines, else a plain line. */
  rows: AssignmentLine[];
  needs: Map<string, NeedGroup>;
  /** SO line id -> its need's id, for lines grouped under a need. */
  needOf: Map<string, string>;
  /** Item × location needs per section, a single-line need counting once. */
  itemCounts: Record<BuyingOrderLineType, number>;
  /** Lines per section, a need's stock lines counting in the need's. */
  lineCounts: Record<BuyingOrderLineType, number>;
}

export function isNeedId(id: string | null | undefined): id is string {
  return id?.startsWith(NEED_ID_PREFIX) ?? false;
}

/** A line a need-row action may change: pending, so not trashed or ordered. */
function isOpenNeedLine(line: AssignmentLine): boolean {
  return line.status === 'pending';
}

function itemLocationKey(line: AssignmentLine): string | null {
  if (line.productV3Key == null) return null;
  return `${line.destinationWarehouseId}:${line.productV3Key}`;
}

/** The need a sales-order line belongs to; null for a stock line. */
function needIdOf(line: AssignmentLine): string | null {
  const type = classifyLine(line);
  const key = itemLocationKey(line);
  if (type === 'stock' || key == null) return null;
  return `${NEED_ID_PREFIX}${type}:${key}`;
}

/** Sales-order lines first, oldest requested date first; stock lines last. */
function memberOrder(a: AssignmentLine, b: AssignmentLine): number {
  const aStock = classifyLine(a) === 'stock' ? 1 : 0;
  const bStock = classifyLine(b) === 'stock' ? 1 : 0;
  return aStock - bStock || byRequestedDate(a, b);
}

function byRequestedDate(a: AssignmentLine, b: AssignmentLine): number {
  const da = a.triggerSalesOrderRequestedDate ?? '9999-12-31';
  const db = b.triggerSalesOrderRequestedDate ?? '9999-12-31';
  if (da !== db) return da < db ? -1 : 1;
  return (a.triggerSalesOrderNumber ?? '').localeCompare(
    b.triggerSalesOrderNumber ?? '',
  );
}

/** Every open line agrees on `pick`: that value; otherwise null. */
export function agreedValue<T>(
  lines: readonly AssignmentLine[],
  pick: (line: AssignmentLine) => T,
): { value: T } | null {
  if (lines.length === 0) return null;
  const first = pick(lines[0]);
  return lines.every((line) => Object.is(pick(line), first))
    ? { value: first }
    : null;
}

/**
 * What SO lines must share for the need row to show one value. PPV also shows
 * the cost-constraint cost, so two receive costs read as Mixed.
 */
export function needAgreementKey(
  columnId: string,
  line: AssignmentLine,
  value: unknown,
): unknown {
  if (columnId !== 'ppv') return value;
  return `${String(value)}|${line.buyTerms.costConstraintUnitCost ?? ''}`;
}

/**
 * The lines a need-row action reaches: its open SO lines, or, once none is
 * open, its trashed ones (so Restore brings them back).
 */
function needActionLines(
  group: Pick<NeedGroup, 'lines' | 'open'>,
): AssignmentLine[] {
  if (group.open.length > 0) return group.open;
  return group.lines.filter((line) => line.status === 'rejected');
}

/**
 * The need row's own line: the first open SO line under the need's id, with
 * the state the open lines agree on (else Unapproved), so the row's State
 * menu offers the actions that apply to them. With no open line it is a
 * released line as it stands, else a trashed one.
 */
function needLine(group: NeedGroup): AssignmentLine {
  const base = group.open[0];
  if (base == null) {
    const finished =
      group.lines.find((line) => line.status !== 'rejected') ?? group.lines[0];
    return { ...finished, suggestionId: group.id };
  }
  const agreed = agreedValue(group.open, (line) => line.approvalState);
  return {
    ...base,
    suggestionId: group.id,
    approvalState: agreed?.value ?? 'unapproved',
    troubleReason: agreed?.value === 'trouble' ? base.troubleReason : null,
  };
}

/**
 * Lines per need id, in input order: a need has a sales-order line, and either
 * a second one or a stock line. An item × location's stock lines join its
 * sales-order need (the back order's, else the POTF one): the buyer orders
 * both at once. Stock lines with no sales-order need stay plain.
 */
function membersByNeed(
  lines: readonly AssignmentLine[],
): Map<string, AssignmentLine[]> {
  const members = new Map<string, AssignmentLine[]>();
  const needOfItem = new Map<string, string>();
  for (const line of lines) {
    const id = needIdOf(line);
    if (id == null) continue;
    appendTo(members, id, line);
    claimItem(needOfItem, line, id);
  }
  for (const line of lines) {
    const id = stockNeedId(line, needOfItem);
    if (id != null) appendTo(members, id, line);
  }
  for (const [id, list] of members) {
    if (list.length < 2) members.delete(id);
  }
  return members;
}

function appendTo(
  members: Map<string, AssignmentLine[]>,
  id: string,
  line: AssignmentLine,
): void {
  const list = members.get(id);
  if (list) list.push(line);
  else members.set(id, [line]);
}

/** The need an item × location's stock lines join: its back order first. */
function claimItem(
  needOfItem: Map<string, string>,
  line: AssignmentLine,
  id: string,
): void {
  const key = itemLocationKey(line);
  if (key == null) return;
  if (!needOfItem.has(key) || classifyLine(line) === 'back_order') {
    needOfItem.set(key, id);
  }
}

function stockNeedId(
  line: AssignmentLine,
  needOfItem: ReadonlyMap<string, string>,
): string | undefined {
  if (classifyLine(line) !== 'stock') return undefined;
  const key = itemLocationKey(line);
  return key == null ? undefined : needOfItem.get(key);
}

function toNeedGroup(
  id: string,
  members: readonly AssignmentLine[],
): NeedGroup {
  const sorted = [...members].sort(memberOrder);
  return {
    id,
    // Sorted sales-order lines first, so this is the need's own type.
    type: classifyLine(sorted[0]),
    lines: sorted,
    open: sorted.filter(isOpenNeedLine),
    salesOrderCount: sorted.filter((line) => classifyLine(line) !== 'stock')
      .length,
  };
}

/**
 * The lines the board groups into needs: `shown` (the page's allowed types)
 * plus, when the page hides stock, the stock lines at an item × location
 * whose SO lines it shows. A need's total spreads over its stock line, so it
 * must see that line even with Stock filtered out, or each raise would add
 * another hidden one.
 */
export function withNeedStockLines(
  all: readonly AssignmentLine[],
  shown: readonly AssignmentLine[],
): AssignmentLine[] {
  const shownIds = new Set(shown.map((line) => line.suggestionId));
  const needItems = new Set(
    shown.flatMap((line) =>
      needIdOf(line) == null ? [] : [itemLocationKey(line)],
    ),
  );
  const hiddenStock = all.filter((line) => {
    if (shownIds.has(line.suggestionId) || classifyLine(line) !== 'stock') {
      return false;
    }
    const key = itemLocationKey(line);
    return key != null && needItems.has(key);
  });
  return hiddenStock.length === 0 ? [...shown] : [...shown, ...hiddenStock];
}

/**
 * Folds the lines of one item × location (per section) under a need row (see
 * membersByNeed). `lines` arrive in section order (see groupLines), so a need
 * row takes its first sales-order line's place, and its stock lines leave the
 * Stock section to sit under it.
 */
export function buildNeedRows(lines: readonly AssignmentLine[]): NeedRows {
  const members = membersByNeed(lines);
  const memberOf = new Map<string, string>();
  for (const [id, list] of members) {
    for (const line of list) memberOf.set(line.suggestionId, id);
  }
  const result: NeedRows = {
    rows: [],
    needs: new Map(),
    needOf: new Map(),
    itemCounts: { back_order: 0, potf: 0, stock: 0 },
    lineCounts: { back_order: 0, potf: 0, stock: 0 },
  };
  for (const line of lines) {
    const id = memberOf.get(line.suggestionId);
    const group = id == null ? undefined : members.get(id);
    if (id == null || group == null) {
      result.itemCounts[classifyLine(line)] += 1;
      result.lineCounts[classifyLine(line)] += 1;
      result.rows.push(line);
    } else if (!result.needs.has(id)) {
      addNeed(result, toNeedGroup(id, group));
    }
  }
  return result;
}

function addNeed(result: NeedRows, need: NeedGroup): void {
  result.needs.set(need.id, need);
  result.itemCounts[need.type] += 1;
  result.lineCounts[need.type] += need.lines.length;
  result.rows.push(needLine(need), ...need.lines);
  for (const child of need.lines)
    result.needOf.set(child.suggestionId, need.id);
}

/**
 * Line ids with each need id swapped for the SO lines its actions reach: the
 * open ones, else the trashed ones.
 */
export function expandNeedIds(
  ids: readonly string[],
  needs: ReadonlyMap<string, NeedGroup>,
): string[] {
  const out = new Set<string>();
  for (const id of ids) {
    if (!isNeedId(id)) {
      out.add(id);
      continue;
    }
    const need = needs.get(id);
    if (!need) continue;
    for (const line of needActionLines(need)) out.add(line.suggestionId);
  }
  return [...out];
}

/**
 * The line the Line section shows for a selected need row: its first open
 * SO line (else its first line), found among the board's lines by the need
 * id's item × location and section.
 */
export function needRepresentativeLine(
  lines: readonly AssignmentLine[],
  needId: string,
): AssignmentLine | null {
  const matches = lines
    .filter((line) => needIdOf(line) === needId)
    .sort(memberOrder);
  return matches.find(isOpenNeedLine) ?? matches[0] ?? null;
}

/**
 * The row to mark selected: the selected id, or, for a need row a column sort
 * has flattened away, its representative SO line (so the highlight and ↑/↓
 * carry on from it).
 */
export function shownSelectedLineId(
  selectedLineId: string | undefined,
  visibleLineIds: readonly string[],
  lines: readonly AssignmentLine[],
): string | undefined {
  if (!isNeedId(selectedLineId) || visibleLineIds.includes(selectedLineId)) {
    return selectedLineId;
  }
  return needRepresentativeLine(lines, selectedLineId)?.suggestionId;
}

/** The section a row sits in: its need's, for a need row or a line under one. */
export function sectionOf(
  line: AssignmentLine,
  needRows: Pick<NeedRows, 'needs' | 'needOf'> | null,
): BuyingOrderLineType {
  const id = line.suggestionId;
  const need = needRows?.needs.get(needRows.needOf.get(id) ?? id);
  return need?.type ?? classifyLine(line);
}
